import { useEffect, useMemo, useRef, useState } from "react";
import {
  archiveProject,
  updateProject,
  type Project,
  type ProjectCliLogins,
  type ProjectDirectory,
} from "@/api/spaces";
import {
  Button,
  AutoResizeTextarea,
  ColorPicker,
  ConfirmDialog,
  PocketRow,
  PocketSection,
  PRESET_COLORS,
} from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import { useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { settingsProjectsMessages } from "@/i18n/messages/settings-projects";
import { HOME_PROJECT_COLOR } from "@/lib/home-project-colors";
import { isTauri } from "@/lib/is-tauri";
import { openProjectWorkspace } from "@/lib/open-project-workspace";
import { useNotificationStore } from "@/stores/notification-store";
import { useProjectStore } from "@/stores/project-store";
import { claudeLoginOf } from "./project-claude-login";
import { ProjectClaudeLoginSection } from "./project-claude-login-section";
import {
  useSettingsLeaveGuard,
  useSettingsNav,
  type SettingsLeaveGuard,
} from "./settings-navigation";
import {
  useResetSettingsDraft,
  useSettingsDraftState,
} from "./settings-draft-state";
import { SettingsFieldError, SettingsTextField } from "./settings-text-field";
import {
  projectError,
  ProjectPageUnavailable,
  refreshProjectNavigation,
  useProjectPage,
} from "./project-page-state";

type ProjectPatch = Parameters<typeof updateProject>[1];

interface ProjectForm {
  name: string;
  description: string;
  color: string;
  cliLogins: ProjectCliLogins;
}

function formOf(project: Project): ProjectForm {
  return {
    name: project.name,
    description: project.description,
    color: project.color,
    cliLogins: project.cli_logins ?? {},
  };
}

function newProjectForm(): ProjectForm {
  // Like the server, a new project starts on a random preset colour.
  const color =
    PRESET_COLORS[Math.floor(Math.random() * PRESET_COLORS.length)] ?? "";
  return { name: "", description: "", color, cliLogins: {} };
}

/** The fields of `form` that differ from `saved`, as one update. */
function changesFrom(saved: ProjectForm, form: ProjectForm): ProjectPatch {
  const patch: ProjectPatch = {};
  if (form.name.trim() !== saved.name) patch.name = form.name.trim();
  if (form.description !== saved.description)
    patch.description = form.description;
  if (form.color !== saved.color) patch.color = form.color;
  if (claudeLoginOf(form.cliLogins) !== claudeLoginOf(saved.cliLogins))
    patch.cli_logins = form.cliLogins;
  return patch;
}

/** One project's settings, or a new project when `projectId` is null. */
export function ProjectPage({ projectId }: { projectId: string | null }) {
  const state = useProjectPage(projectId ?? "");
  if (projectId === null)
    return <ProjectEditor project={null} directories={[]} />;
  if (state.loading || !state.project)
    return (
      <ProjectPageUnavailable loading={state.loading} retry={state.retry} />
    );
  return (
    <ProjectEditor
      key={projectId}
      project={state.project}
      directories={state.directories}
    />
  );
}

/** Nothing is written until Create or Save; leaving with edits asks first. */
function ProjectEditor({
  project,
  directories,
}: {
  project: Project | null;
  directories: ProjectDirectory[];
}) {
  const t = useT(settingsProjectsMessages);
  const tc = useT(commonMessages);
  const nav = useSettingsNav();
  const draftPrefix = `project:${project?.id ?? "new"}:`;
  const resetDraft = useResetSettingsDraft();
  // Null until the project exists; Create fills it before its last write.
  const [saved, setSaved] = useState(project);
  const [fresh] = useState(() =>
    project ? formOf(project) : newProjectForm(),
  );
  const [form, setForm] = useSettingsDraftState(`${draftPrefix}form`, fresh);
  const changes = changesFrom(saved ? formOf(saved) : fresh, form);
  const dirty = Object.keys(changes).length > 0;
  const nameMissing = !form.name.trim();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const writing = useRef(false);
  const [archive, setArchive] = useState(false);
  const [pendingLeave, setPendingLeave] = useState<(() => void) | null>(null);
  const guard = useMemo<SettingsLeaveGuard | null>(
    () => (dirty ? (leave) => setPendingLeave(() => leave) : null),
    [dirty],
  );
  const release = useSettingsLeaveGuard(guard);
  const edit = (patch: Partial<ProjectForm>) =>
    setForm((current) => ({ ...current, ...patch }));
  const shown = useRef(true);
  useEffect(() => {
    shown.current = true;
    return () => {
      shown.current = false;
    };
  }, []);

  /** Leaves without asking: the edits are saved or discarded. A write that
   * ends after the user already left must not move them again. */
  function finish(leave: () => void) {
    if (!shown.current) return;
    resetDraft(draftPrefix);
    release();
    leave();
  }
  async function run(action: () => Promise<void>, fallback: () => string) {
    if (writing.current) return;
    writing.current = true;
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (failure) {
      setArchive(false);
      setError(projectError(failure, fallback()));
    } finally {
      writing.current = false;
      setBusy(false);
    }
  }
  function create() {
    let failed = t("createProjectError");
    return run(
      async () => {
        const created = await useProjectStore
          .getState()
          .createProjectWithDefaults(form.name.trim());
        // It exists now: a failed update below leaves this page on it, unsaved.
        setSaved(created);
        failed = t("updateProjectError");
        const rest = changesFrom(formOf(created), {
          ...form,
          name: created.name,
        });
        if (Object.keys(rest).length) await updateProject(created.id, rest);
        refreshProjectNavigation();
        finish(nav.back);
      },
      () => failed,
    );
  }
  function save(projectId: string) {
    return run(
      async () => {
        await updateProject(projectId, changes);
        refreshProjectNavigation();
        useNotificationStore.getState().addSystemToast({
          id: `settings-project-saved-${projectId}`,
          title: t("saved"),
          level: "info",
        });
        finish(nav.back);
      },
      () => t("updateProjectError"),
    );
  }
  /** Shows the project's workspace; unsaved edits ask to be discarded first. */
  function openProject(project: Project) {
    const leave = () => {
      nav.close();
      void openProjectWorkspace(project);
    };
    if (dirty) setPendingLeave(() => leave);
    else finish(leave);
  }
  return (
    <div data-testid="settings-page-project">
      <PocketSection testId="project-details">
        <div data-testid="project-name-row">
          <SettingsTextField
            value={form.name}
            onChange={(event) => edit({ name: event.target.value })}
            disabled={busy}
            label={t("name")}
            aria-label={t("projectName")}
            aria-invalid={Boolean(saved && nameMissing)}
            error={saved && nameMissing ? t("enterProjectName") : null}
            autoFocus={!saved}
            testId="project-name"
          />
        </div>
        <div data-testid="project-description-row">
          <label className="block px-2 py-1.5 text-appkit-small font-medium text-muted">
            {t("description")}
            <AutoResizeTextarea
              value={form.description}
              onChange={(event) => edit({ description: event.target.value })}
              disabled={busy}
              aria-label={t("description")}
              data-testid="project-description"
              className="mt-1.5"
            />
          </label>
        </div>
        <PocketRow
          testId="project-color-row"
          title={t("colour")}
          trailing={
            saved?.is_home ? (
              <span
                aria-label={t("homeProjectColour")}
                className="h-5 w-5 rounded-full"
                style={{ backgroundColor: HOME_PROJECT_COLOR }}
                data-testid="project-home-color"
              />
            ) : (
              <div
                aria-busy={busy}
                className={busy ? "pointer-events-none" : undefined}
              >
                <ColorPicker
                  value={form.color}
                  onChange={(color) => edit({ color })}
                  data-testid="project-color"
                />
              </div>
            )
          }
        />
      </PocketSection>
      {saved ? (
        <PocketSection title={t("folders")} testId="project-folders">
          {directories.map((folder) => (
            <PocketRow
              key={folder.id}
              testId={`project-folder-${folder.id}`}
              leading={<Icons.Folder className="h-4 w-4 text-muted" />}
              title={
                folder.label ||
                folder.path.split(/[\\/]/).filter(Boolean).slice(-1)[0] ||
                folder.path
              }
              secondLine={folder.path}
              chevron
              onSelect={() =>
                nav.push({
                  page: "projects-folder",
                  args: {
                    projectId: saved.id,
                    folderId: folder.id,
                    title: folder.label || t("folder"),
                  },
                })
              }
            />
          ))}
          <PocketRow
            testId="project-add-folder"
            title={t("addFolder")}
            leading={<Icons.Plus className="h-4 w-4 text-muted" />}
            onSelect={() =>
              nav.push({
                page: "projects-add-folder",
                args: { projectId: saved.id },
              })
            }
          />
        </PocketSection>
      ) : (
        <p
          className="px-2 py-1.5 text-appkit-small text-muted"
          data-testid="project-folders-after-create"
        >
          {t("foldersAfterCreate")}
        </p>
      )}
      {isTauri() && (
        <ProjectClaudeLoginSection
          title={t("account")}
          cliLogins={form.cliLogins}
          disabled={busy}
          onChange={(cliLogins) => edit({ cliLogins })}
        />
      )}
      <SettingsFieldError error={error} testId="project-error" />
      <div className="flex items-center justify-end gap-1.5 px-2 pt-1.5">
        {saved && !saved.is_home && (
          <Button
            size="sm"
            variant="ghost"
            className="mr-auto text-danger"
            disabled={busy}
            data-testid="project-archive"
            onClick={() => setArchive(true)}
          >
            {t("archiveProject")}
          </Button>
        )}
        {saved && (
          <Button
            size="sm"
            variant="secondary"
            disabled={busy}
            data-testid="project-open"
            onClick={() => openProject(saved)}
          >
            {t("openProject")}
          </Button>
        )}
        {saved ? (
          <Button
            size="sm"
            disabled={busy || !dirty || nameMissing}
            data-testid="project-save"
            onClick={() => void save(saved.id)}
          >
            {tc("save")}
          </Button>
        ) : (
          <Button
            size="sm"
            disabled={busy || nameMissing}
            data-testid="project-create-submit"
            onClick={() => void create()}
          >
            {busy ? t("creating") : t("create")}
          </Button>
        )}
      </div>
      {archive && saved && (
        <ConfirmDialog
          busy={busy}
          title={t("archiveProjectTitle")}
          description={t("archiveProjectDescription")}
          confirmLabel={t("archive")}
          data-testid="project-archive-confirm"
          onCancel={() => {
            if (!busy) setArchive(false);
          }}
          onConfirm={() =>
            void run(
              async () => {
                await archiveProject(saved.id);
                refreshProjectNavigation();
                finish(() => nav.open({ page: "projects" }));
              },
              () => t("updateProjectError"),
            )
          }
        />
      )}
      {pendingLeave && (
        <ConfirmDialog
          title={t("discardChangesTitle")}
          description={t("discardChangesDescription")}
          confirmLabel={t("discard")}
          data-testid="project-discard-confirm"
          onCancel={() => setPendingLeave(null)}
          onConfirm={() => {
            setPendingLeave(null);
            finish(pendingLeave);
          }}
        />
      )}
    </div>
  );
}
